import { useEffect, useState } from "react";
import { isNumber } from "../Table/FastTable";
import { GET } from "../api/BackendApi";
import { Participant, GetParticipantDto } from "../api/Dtos";
import { useGlobalContext } from "../Layout/GlobalState";

import * as ExcelJS from 'exceljs';

export async function downloadExcel(data: any[], fileName: string, columnWidths?: number[], pageHeader?: string, repeatHeaderRows?: number) {
  // Convert data to array of arrays format
  const dataArray: any[][] = [];
  
  // Add page header if provided
  if (pageHeader) {
    dataArray.push([pageHeader]); // Title row
    dataArray.push([]); // Blank row for spacing
  }

  // Add header row
  const headers = Object.keys(data[0] || {});
  dataArray.push(headers);

  // Add data rows
  data.forEach(row => {
    dataArray.push(headers.map(header => row[header] || ''));
  });

  // Create new workbook and worksheet
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Blad1');

  // Add data to worksheet
  dataArray.forEach((row, index) => {
    worksheet.getRow(index + 1).values = row;
  });

  // Set print header and footer
  worksheet.headerFooter.oddHeader = '&"Arial,Bold"&18Östhammar Friidrott - Stadsloppet';
  worksheet.headerFooter.oddFooter = 'Sida &P av &N';

  // Set column widths
  if (columnWidths && columnWidths.length > 0) {
    columnWidths.forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width;
    });
  } else {
    // Default widths
    headers.forEach((header, index) => {
      const maxLength = Math.max(
        header.length,
        ...data.map(row => String(row[header] || '').length)
      );
      worksheet.getColumn(index + 1).width = Math.min(maxLength + 2, 50);
    });
  }

  // Set print titles (repeat rows)
  if (repeatHeaderRows && repeatHeaderRows > 0) {
    worksheet.pageSetup.printTitlesRow = `1:${repeatHeaderRows}`;
  }

  if (pageHeader) {
    worksheet.getCell('A1').font = { size: 12, bold: true };
  }

  // Generate and download the file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}



export function useParticipantFilter(allParticipants: Array<Participant>) {
  const [filterClass, setFilterClass] = useState<string | undefined>(undefined);
  const [filterStartnrOrName, setFilterStartnrOrName] = useState<
    string | undefined
  >(undefined);

  const [filteredParticipants, setFilteredParticipants] = useState<
    Array<Participant>
  >([]);

  useEffect(() => {
    setFilteredParticipants(allParticipants);
  }, [allParticipants]);

  // Apply filtering
  useEffect(() => {
    let newParticipantArray = [...allParticipants];
    if (filterClass) {
      newParticipantArray = newParticipantArray.filter(
        (p) => p.disciplineName === filterClass
      );
    }

    if (filterStartnrOrName) {
      if (isNumber(filterStartnrOrName)) {
        newParticipantArray = newParticipantArray.filter((p) => {
          return `${p.startnumber}`.startsWith(filterStartnrOrName);
        });
      } else {
        newParticipantArray = newParticipantArray.filter((p) =>
          `${p.firstname} ${p.lastname}`.includes(filterStartnrOrName)
        );
      }
    }

    setFilteredParticipants(newParticipantArray);
  }, [allParticipants, filterClass, filterStartnrOrName]);

  return {
    toggleClassFilter: (newVal: string) => {setFilterClass(filterClass === newVal ? undefined : newVal)}, 
    clearClassFilter: () => setFilterClass(undefined),
    toggleOnStartnrOrName: (newVal: string) => {setFilterStartnrOrName(filterStartnrOrName === newVal ? undefined : newVal)}, 
    clearStartnrOrNameFilter: () => setFilterStartnrOrName(undefined),
    filteredParticipants
  }
}

export function useParticipantsFromBackend() {
  const [participants, setParticipants] = useState<Array<Participant>>([]);
  const [loading, setLoading] = useState(false);
  const globalContext = useGlobalContext();

  // Get participants from backend
  useEffect(() => {
    if (globalContext.competition) {
      setLoading(true);
      GET.allParticipants(globalContext.competition.id, globalContext.competition.disciplines, globalContext.competition.startgroups).then(
        //(response: GetParticipantDto) => {
        (response: any) => {
          setParticipants(response.participants);
        }
      ).catch((e: Error) => {
        console.log(e);
      }).finally(() => {
        setLoading(false);
      });
    }
  }, []);

  return {participants, setParticipants, loading};
}

